import React, { useEffect, useState } from 'react';
import {
  getMonitor, getMonitorAccess, getPedidosDeWhatsapp, liberarWhatsapp, PedidoDeWhatsapp,
  getFilaDeSuporte, resolverPedidoDeSuporte, regerarDiagnosticoDeSuporte, PedidoDeSuporte,
  getPainelDeAcessoAoBanco, desconectarBancosDe, desligarConexaoOrfa, ConexaoOrfa, PainelDeAcessoAoBanco, PessoaNoAcesso, Monitor, SaudeDeJob, RetratoDoBackup, registrarBackup, SugestaoRecebida, EstadoDaSugestao, VereditoJob, EtapaDoCadastro, DegrauDoFunil,
} from '../services/api';
import { useConfirmar } from '../contexts/Confirmacao';
import './PaginaDeSaude.css';
import PlanosNoMonitor from './PlanosNoMonitor';

/** O endereço da página. Fica fora do `App` principal: ver `App.tsx`. */
export const CAMINHO_DA_SAUDE = '/saude';
export const ehPaginaDeSaude = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '') === CAMINHO_DA_SAUDE;

/**
 * O monitor.
 *
 * ── Por que virou página (22/10) ───────────────────────────────────────────
 *
 * Era um modal, e cresceu seção a seção até virar uma rolagem de oito
 * assuntos: rotinas, uso, planos, Open Finance, suporte, WhatsApp, sugestões,
 * infraestrutura. Agora mora em `/saude`, com um menu e uma seção por vez. O
 * menu leva a contagem do que espera por ela, então a fila de alguém não fica
 * escondida só porque a seção não está aberta. A seção aberta vai no `#` da
 * URL: recarregar ou mandar o link cai no mesmo lugar.
 *
 * ── Por que ele julga em vez de listar ─────────────────────────────────────
 *
 * O padrão de falha deste app é SILÊNCIO. O relatório mensal ficou parado
 * desde fevereiro sem erro nenhum. O alerta de limite nunca funcionou e três
 * `.catch(() => {})` engoliram o motivo. O canal do WhatsApp caiu 4 horas e a
 * tela seguiu dizendo que estava tudo ligado.
 *
 * Uma tela que só mostrasse "última execução: 12/02" transferiria o julgamento
 * para quem está olhando — e quem está olhando não sabe de cor qual job roda
 * todo dia e qual roda todo mês. Então cada linha vem com veredito, e o topo
 * responde em uma palavra.
 */

const ROTULO_VEREDITO: Record<VereditoJob, string> = {
  ok: 'em dia',
  atrasado: 'atrasado',
  falhando: 'falhando',
  'nunca-rodou': 'nunca rodou',
};

const quando = (iso: string | null, horas: number | null): string => {
  if (!iso) return 'nunca';
  if (horas === null) return new Date(iso).toLocaleString('pt-BR');
  if (horas < 1) return 'há minutos';
  if (horas < 24) return `há ${Math.round(horas)}h`;
  return `há ${Math.round(horas / 24)} dia${Math.round(horas / 24) === 1 ? '' : 's'}`;
};

const diaBR = (iso: string): string => {
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
};

/** "em 7 dias", "hoje", "venceu há 3 dias". */
const prazoDoBackup = (b: RetratoDoBackup): string => {
  if (b.diasParaVencer === null || !b.venceEm) return 'nunca registrado';
  const feito = `feito em ${diaBR(b.ultimoEm!)}`;
  if (b.diasParaVencer > 1) return `${feito} · próximo em ${diaBR(b.venceEm)} (daqui a ${b.diasParaVencer} dias)`;
  if (b.diasParaVencer === 1) return `${feito} · próximo amanhã`;
  if (b.diasParaVencer === 0) return `${feito} · vence hoje`;
  const atraso = -b.diasParaVencer;
  return `${feito} · venceu há ${atraso} dia${atraso === 1 ? '' : 's'}`;
};

const LinhaJob: React.FC<{ j: SaudeDeJob }> = ({ j }) => (
  <div className={`mon-job mon-v-${j.veredito}`}>
    <span className="mon-job-nome">{j.rotulo}</span>
    <span className="mon-job-veredito">{ROTULO_VEREDITO[j.veredito]}</span>
    <span className="mon-job-quando">{quando(j.ultimaEm, j.horasDesde)}</span>
    {/* O motivo da falha vem junto e não escondido atrás de um clique: foi
        justamente motivo engolido que deixou o alerta de limite quebrado
        desde sempre sem ninguém saber. */}
    {j.detalhe && <span className="mon-job-detalhe">{j.detalhe}</span>}
  </div>
);

/**
 * Uma sugestão recebida.
 *
 * O texto vai CRU e inteiro. A frase dela é o único registro fiel do que
 * faltou, e cortar em três linhas com reticências esconde justamente a parte
 * que ninguém previu — que é a razão de a coleta existir.
 *
 * O selo de "ninguém foi avisado" é o motivo desta seção existir: sem ele,
 * uma sugestão cujo email falhou não aparecia em lugar nenhum do app.
 */
/**
 * Só a atrasada ganha selo.
 *
 * `na-fila` é o estado normal de toda sugestão nova — ela espera o resumo de
 * segunda. Marcar isso de âmbar faria o alarme acender em toda sugestão até
 * segunda-feira, e alarme que sempre acende deixa de ser lido. É a mesma razão
 * pela qual a sonda do canal só avisa na transição.
 */
/** Onde a conta parou, dito na lista dos novos. */
const PARADA: Record<EtapaDoCadastro, string> = {
  plano: 'não escolheu plano',
  pagamento: 'escolheu, não pagou',
  banco: 'sem banco conectado',
  lancamento: 'ainda não lançou',
};

const DEGRAU: Record<DegrauDoFunil['etapa'], string> = {
  entrou: 'Entraram',
  plano: 'Escolheram plano',
  pagamento: 'Plano liberado',
  banco: 'Conectaram banco (Completo)',
  lancamento: 'Lançaram algo',
};

const SELO: Partial<Record<EstadoDaSugestao, string>> = {
  atrasada: 'ficou fora do resumo',
};

const LinhaSugestao: React.FC<{ s: SugestaoRecebida }> = ({ s }) => (
  <div className={`mon-sug mon-sug-${s.estado}`}>
    <p className="mon-sug-texto">{s.texto}</p>
    <div className="mon-sug-pe">
      <span>{s.dePessoa}</span>
      <span>·</span>
      <span>{s.canal}</span>
      <span>·</span>
      <span>
        {s.diasDesde === null ? new Date(s.quando).toLocaleDateString('pt-BR')
          : s.diasDesde === 0 ? 'hoje'
          : `há ${s.diasDesde} dia${s.diasDesde === 1 ? '' : 's'}`}
      </span>
      {SELO[s.estado] && <span className="mon-sug-selo">{SELO[s.estado]}</span>}
    </div>
  </div>
);

type Secao = 'rotinas' | 'uso' | 'planos' | 'open-finance' | 'suporte' | 'whatsapp' | 'sugestoes' | 'infra';

const SECOES: { id: Secao; rotulo: string }[] = [
  { id: 'rotinas', rotulo: 'Rotinas' },
  { id: 'uso', rotulo: 'Quem está usando' },
  { id: 'planos', rotulo: 'Planos' },
  { id: 'open-finance', rotulo: 'Acesso ao Open Finance' },
  { id: 'suporte', rotulo: 'Suporte do Open Finance' },
  { id: 'whatsapp', rotulo: 'Pedidos de WhatsApp' },
  { id: 'sugestoes', rotulo: 'Sugestões' },
  { id: 'infra', rotulo: 'Infraestrutura' },
];

const secaoDoHash = (): Secao => {
  const h = window.location.hash.replace(/^#/, '');
  return SECOES.some(s => s.id === h) ? (h as Secao) : 'rotinas';
};

/**
 * A porta da página. O item do menu só aparece para quem está em
 * ADMIN_EMAILS, mas o endereço qualquer um digita: aqui ele é conferido de
 * novo antes de montar o painel (o servidor nega de qualquer forma; isto só
 * evita mostrar uma tela de erros a quem nem deveria estar aqui).
 */
const PaginaDeSaude: React.FC = () => {
  const [admin, setAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    let vivo = true;
    getMonitorAccess().then(a => vivo && setAdmin(a));
    return () => { vivo = false; };
  }, []);

  if (admin === null) return <div className="mon-pagina"><p className="mon-nota mon-porta">Carregando…</p></div>;
  if (!admin) {
    return (
      <div className="mon-pagina">
        <p className="mon-nota mon-porta">
          Esta página é só da administração do app. <a href="/">Voltar ao app</a>
        </p>
      </div>
    );
  }
  return <PainelDeSaude />;
};

export const PainelDeSaude: React.FC = () => {
  const confirmar = useConfirmar();

  const [secao, setSecao] = useState<Secao>(secaoDoHash);
  useEffect(() => {
    /* Trocar de seção leva ao topo dela: no celular o menu fica no alto. */
    const mudou = () => { setSecao(secaoDoHash()); window.scrollTo(0, 0); };
    window.addEventListener('hashchange', mudou);
    return () => window.removeEventListener('hashchange', mudou);
  }, []);
  /* No celular o menu é uma faixa que rola de lado: a aba aberta fica à vista. */
  useEffect(() => {
    document.querySelector('.mon-menu-item.ativo')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [secao]);

  const [m, setM] = useState<Monitor | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /*
   * Os pedidos de WhatsApp vêm numa chamada à parte, e falham à parte: o
   * painel existe para dizer se o app está de pé, e uma lista que não carregou
   * não pode levar o resto junto. `null` = ainda não sei.
   */
  const [pedidos, setPedidos] = useState<PedidoDeWhatsapp[] | null>(null);
  const [liberando, setLiberando] = useState<string | null>(null);
  const [erroDoPedido, setErroDoPedido] = useState<string | null>(null);
  /** Quem foi liberada sem que o email de aviso saísse. */
  const [semAviso, setSemAviso] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    getPedidosDeWhatsapp().then(r => vivo && setPedidos(r)).catch(() => vivo && setPedidos(null));
    return () => { vivo = false; };
  }, []);

  /*
   * A fila de suporte do Open Finance. Como os pedidos de WhatsApp, vem e
   * falha à parte: é uma fila em que alguém está esperando resposta, e não
   * pode sumir porque outra parte do painel não carregou.
   */
  const [suporte, setSuporte] = useState<PedidoDeSuporte[] | null>(null);
  const [agindoNoSuporte, setAgindoNoSuporte] = useState<string | null>(null);
  const [recadoDoSuporte, setRecadoDoSuporte] = useState<string | null>(null);

  /**
   * O acesso ao Open Finance: quem está com banco, e as órfãs no provedor.
   * O acesso vem do plano (Asaas); daqui só se desconecta. A fila de 08/10,
   * com o "Pix conferido: liberar", saiu em 23/10.
   */
  const [acessoAoBanco, setAcessoAoBanco] = useState<PainelDeAcessoAoBanco | null>(null);
  const [agindoNoAcesso, setAgindoNoAcesso] = useState<string | null>(null);
  const [recadoDoAcesso, setRecadoDoAcesso] = useState<string | null>(null);
  const carregarAcessoAoBanco = () =>
    getPainelDeAcessoAoBanco().then(setAcessoAoBanco).catch(() => setAcessoAoBanco(null));
  useEffect(() => { void carregarAcessoAoBanco(); }, []);

  const agirNoAcesso = async (p: PessoaNoAcesso, acao: () => Promise<void>, depois: string) => {
    setAgindoNoAcesso(p.userId);
    setRecadoDoAcesso(null);
    try {
      await acao();
      await carregarAcessoAoBanco();
      setRecadoDoAcesso(depois);
    } catch (e: any) {
      setRecadoDoAcesso(e?.response?.data?.error ?? 'Não consegui fazer isso agora.');
    } finally {
      setAgindoNoAcesso(null);
    }
  };

  /**
   * A linha é o plano pago: o botão desliga só os bancos, e o plano
   * continua (23/10). Encerrar o plano é pelo plano.
   */
  const desconectarBancos = async (p: PessoaNoAcesso) => {
    if (!await confirmar({
      titulo: 'Desconectar os bancos desta pessoa?',
      alvo: p.nome || p.email || 'conta sem e-mail',
      texto: 'O banco dela é desconectado no provedor e a leitura para. O plano continua como está, e ela pode conectar de novo. Os lançamentos que já entraram continuam dela.',
      acao: 'Desconectar',
      perigo: true,
    })) return;
    await agirNoAcesso(p, () => desconectarBancosDe(p.userId), 'Bancos desconectados. O plano continua.');
  };

  /** A órfã custa e ninguém vê: desligar pergunta antes, e o servidor confere de novo. */
  const desligarOrfa = async (o: ConexaoOrfa) => {
    if (!await confirmar({
      titulo: 'Desligar esta conexão no provedor?',
      alvo: `${o.banco} · ${o.nome || o.email || o.userId}`,
      texto: 'Ela existe no provedor e não está em nenhuma conta do app. Se a pessoa estiver conectando agora, espere alguns minutos: o servidor confere de novo antes de desligar.',
      acao: 'Desligar',
      perigo: true,
    })) return;
    setAgindoNoAcesso(o.conexaoId);
    setRecadoDoAcesso(null);
    try {
      await desligarConexaoOrfa(o.conexaoId);
      await carregarAcessoAoBanco();
      setRecadoDoAcesso('Conexão desligada no provedor.');
    } catch (e: any) {
      setRecadoDoAcesso(e?.response?.data?.error ?? 'Não consegui fazer isso agora.');
    } finally {
      setAgindoNoAcesso(null);
    }
  };

  const carregarSuporte = () => getFilaDeSuporte().then(setSuporte).catch(() => setSuporte(null));
  useEffect(() => { void carregarSuporte(); }, []);

  const NOME_DO_TIPO: Record<string, string> = {
    faltou: 'Faltou uma transação', parcelada: 'Parcelada veio errada', outro: 'Outra coisa',
  };

  /** O pedido inteiro em texto, para colar onde ela investiga. */
  const textoDoPedido = (p: PedidoDeSuporte): string => [
    `Pedido de suporte do Open Finance (${new Date(p.criadoEm).toLocaleString('pt-BR')})`,
    `Tipo: ${NOME_DO_TIPO[p.tipo] ?? p.tipo}`,
    `Banco: ${p.banco}`,
    ...(p.valorCentavos ? [`Valor informado: R$ ${(p.valorCentavos / 100).toFixed(2).replace('.', ',')}`] : []),
    '',
    'O que a pessoa escreveu:',
    p.mensagem,
    '',
    p.diagnostico ? 'Diagnóstico da leitura:' : 'Diagnóstico: ainda não gerado.',
    ...(p.diagnostico ? [JSON.stringify(p.diagnostico, null, 1)] : []),
  ].join('\n');

  const copiarPedido = async (p: PedidoDeSuporte) => {
    try {
      await navigator.clipboard.writeText(textoDoPedido(p));
      setRecadoDoSuporte('Pedido copiado.');
    } catch {
      setRecadoDoSuporte('Não consegui copiar. Tente de novo.');
    }
  };

  const agirNoSuporte = async (p: PedidoDeSuporte, acao: () => Promise<void>, depois: string) => {
    setAgindoNoSuporte(p.id);
    setRecadoDoSuporte(null);
    try {
      await acao();
      await carregarSuporte();
      setRecadoDoSuporte(depois);
    } catch (e: any) {
      setRecadoDoSuporte(e?.response?.data?.error ?? 'Não consegui fazer isso agora.');
    } finally {
      setAgindoNoSuporte(null);
    }
  };

  const liberar = async (p: PedidoDeWhatsapp) => {
    setLiberando(p.userId);
    setErroDoPedido(null);
    try {
      const { avisada } = await liberarWhatsapp(p.userId);
      setPedidos(lista => (lista ?? []).filter(x => x.userId !== p.userId));
      // Liberou de qualquer jeito. O que ela precisa saber é se a pessoa foi
      // avisada — senão o pedido atendido fica sem ninguém sabendo.
      setSemAviso(avisada ? null : (p.nome || p.email));
    } catch (e: any) {
      setErroDoPedido(e?.response?.data?.error ?? 'Não consegui liberar.');
    } finally {
      setLiberando(null);
    }
  };

  const [registrandoBackup, setRegistrandoBackup] = useState(false);
  const [erroDoBackup, setErroDoBackup] = useState<string | null>(null);
  const marcarBackup = async () => {
    setRegistrandoBackup(true);
    setErroDoBackup(null);
    try {
      const backup = await registrarBackup();
      setM(atual => {
        if (!atual) return atual;
        // O selo do topo só pintava "atenção" por causa do backup? Volta a "em dia".
        const soPeloBackup = atual.geral === 'atencao' && atual.backup?.pendente
          && !atual.jobs.some(j => j.veredito === 'atrasado' || j.veredito === 'falhando')
          && atual.canal?.estado !== 'fora-do-ar';
        return { ...atual, backup, geral: soPeloBackup ? 'ok' : atual.geral };
      });
    } catch (e: any) {
      setErroDoBackup(e?.response?.data?.error ?? 'Não consegui registrar o backup.');
    } finally {
      setRegistrandoBackup(false);
    }
  };

  useEffect(() => {
    let vivo = true;
    getMonitor()
      .then(r => vivo && setM(r))
      .catch(e => vivo && setErro(e?.response?.data?.error ?? 'Não consegui ler o monitor.'));
    return () => { vivo = false; };
  }, []);

  /*
   * O que espera por ela em cada seção. Vai no menu para que uma fila não
   * dependa de a seção estar aberta. Zero não aparece: um "0" fixo vira
   * cenário, e o dia em que virar 3 passa despercebido.
   */
  const pendentes: Partial<Record<Secao, number>> = {
    rotinas: m ? m.jobs.filter(j => j.veredito !== 'ok').length + (m.backup?.pendente ? 1 : 0) : undefined,
    'open-finance': acessoAoBanco ? (acessoAoBanco.orfas?.length ?? 0) : undefined,
    suporte: suporte?.length,
    whatsapp: pedidos?.length,
    sugestoes: m?.sugestoes?.atrasadas,
    infra: m ? ((m.banco.ok ? 0 : 1) + (m.protecao && !m.protecao.protegido ? 1 : 0)
      + (m.canal?.estado === 'fora-do-ar' ? 1 : 0)) : undefined,
  };

  return (
    <div className="mon-pagina">
      <header className={`mon-banda ${m ? `mon-geral-${m.geral}` : ''}`}>
        <a className="mon-voltar" href="/">← App</a>
        <h1>Saúde do app</h1>
        {m && (
          <span className="mon-geral-selo">
            {m.geral === 'ok' ? 'tudo em dia' : m.geral === 'atencao' ? 'atenção' : 'algo quebrado'}
          </span>
        )}
      </header>

      <div className="mon-layout">
        <nav className="mon-menu" aria-label="Seções">
          {SECOES.map(s => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={`mon-menu-item${secao === s.id ? ' ativo' : ''}`}
              aria-current={secao === s.id ? 'page' : undefined}
            >
              <span>{s.rotulo}</span>
              {(pendentes[s.id] ?? 0) > 0 && <span className="mon-menu-n">{pendentes[s.id]}</span>}
            </a>
          ))}
        </nav>

        <main className="mon-corpo">
          {erro && <p className="mon-erro">{erro}</p>}
          {!m && !erro && <p className="mon-nota">Carregando…</p>}

          {m && (
            <>
              {secao === 'rotinas' && <section className="mon-secao">
                <h3>Rotinas</h3>
                {/*
                  * O backup do banco é a única rotina feita à mão (22/10): o
                  * `pg_dump` semanal para o cofre no Mac. Vence no sétimo dia,
                  * sem a folga dos jobs, e o botão é como ela diz que fez.
                  */}
                {m.backup && (
                  <div className={`mon-linha mon-backup ${m.backup.pendente ? 'mon-v-atrasado' : 'mon-v-ok'}`}>
                    <span>Backup do banco</span>
                    <span>
                      {prazoDoBackup(m.backup)}
                      {' '}
                      <button
                        type="button"
                        className="mon-pedido-liberar"
                        onClick={marcarBackup}
                        disabled={registrandoBackup}
                        aria-busy={registrandoBackup}
                      >
                        Fiz o backup hoje
                      </button>
                    </span>
                  </div>
                )}
                {erroDoBackup && <p className="mon-erro" role="status">{erroDoBackup}</p>}
                {m.jobs.map(j => <LinhaJob key={j.nome} j={j} />)}
                <p className="mon-nota">
                  O atraso só acusa depois do dobro da cadência, porque o Render
                  hiberna e cobrar pontualidade viraria alarme falso diário.
                </p>
              </section>}

              {secao === 'uso' && !m.usuarios && <p className="mon-nota">Sem dados de uso.</p>}
              {secao === 'uso' && m.usuarios && (
                <section className="mon-secao">
                  <h3>Quem está usando</h3>
                  <div className="mon-numeros">
                    <div><strong>{m.usuarios.ativos7d}</strong><span>ativos em 7 dias</span></div>
                    <div><strong>{m.usuarios.ativos30d}</strong><span>em 30 dias</span></div>
                    <div><strong>{m.usuarios.total}</strong><span>contas</span></div>
                  </div>
                  <div className="mon-pessoas">
                    {m.usuarios.pessoas.map((p, i) => (
                      <div key={i} className={`mon-pessoa${p.escritas7d > 0 ? ' ativa' : ''}`}>
                        <span className="mon-pessoa-nome">{p.nome}</span>
                        <span className="mon-pessoa-quando">
                          {p.diasDesde === null ? 'nunca lançou'
                            : p.diasDesde === 0 ? 'hoje'
                            : `há ${p.diasDesde} dia${p.diasDesde === 1 ? '' : 's'}`}
                        </span>
                        {p.escritas7d > 0 && <span className="mon-pessoa-n">{p.escritas7d}</span>}
                      </div>
                    ))}
                  </div>
                  {/* A definição vem escrita: sem ela, "ativo" viraria o que
                      cada pessoa achar, e login contado como atividade faria o
                      número subir justamente quando o produto piora. */}
                  <p className="mon-nota">
                    Ativo é quem <strong>escreveu</strong>: lançou, criou meta,
                    lembrete, investimento ou dívida. Abrir o app não conta.
                  </p>
                  {m.usuarios.novos && (
                    <div className="mon-novos">
                      <h3>Novos cadastros</h3>
                      <div className="mon-numeros">
                        <div><strong>{m.usuarios.novos.semana}</strong><span>em 7 dias</span></div>
                        <div><strong>{m.usuarios.novos.mes}</strong><span>em 30 dias</span></div>
                      </div>
                      {m.usuarios.novos.funil && m.usuarios.novos.funil[0].chegaram > 0 && (
                        <div className="mon-funil" aria-label="Onde os novos param">
                          {m.usuarios.novos.funil.map(d => (
                            <div key={d.etapa} className="mon-funil-degrau">
                              <span className="mon-funil-rotulo">{DEGRAU[d.etapa]}</span>
                              <span className="mon-funil-barra" aria-hidden="true">
                                <span style={{ width: `${d.de ? (d.chegaram / d.de) * 100 : 0}%` }} />
                              </span>
                              <span className="mon-funil-n">
                                {d.etapa === 'entrou' ? d.chegaram : `${d.chegaram} de ${d.de}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {m.usuarios.novos.funil && !m.usuarios.novos.comPlano && (
                        <p className="mon-nota">A cobrança está desligada: plano, pagamento e banco ficam fora do funil.</p>
                      )}
                      {m.usuarios.novos.itens.length === 0
                        ? <p className="mon-nota">Ninguém se cadastrou nos últimos 30 dias.</p>
                        : (
                          <div className="mon-pessoas">
                            {m.usuarios.novos.itens.map((n, i) => (
                              <div key={i} className={`mon-pessoa${n.jaLancou ? ' ativa' : ''}`}>
                                <span className="mon-pessoa-nome">{n.nome}</span>
                                <span className="mon-pessoa-quando">
                                  {n.diasDesde === 0 ? 'hoje'
                                    : `há ${n.diasDesde} dia${n.diasDesde === 1 ? '' : 's'}`}
                                  {' · '}
                                  {new Date(n.cadastradoEm).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                </span>
                                {n.isenta
                                  ? <span className="mon-pessoa-n mon-pessoa-n-isenta">isenta</span>
                                  : n.parouEm
                                    ? <span className="mon-pessoa-n mon-pessoa-n-parado">{PARADA[n.parouEm]}</span>
                                    : n.parouEm === undefined && !n.jaLancou
                                      && <span className="mon-pessoa-n mon-pessoa-n-parado">ainda não lançou</span>}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  )}
                </section>
              )}

              {/* Os planos do app (20/10). Some sozinha sem o esquema no
                  banco, e aí o `:empty` do CSS diz que não há nada. */}
              {secao === 'planos' && <div className="mon-vazio-se-nada"><PlanosNoMonitor /></div>}

              {secao === 'open-finance' && !acessoAoBanco && (
                <p className="mon-nota">Não consegui ler o acesso ao Open Finance.</p>
              )}
              {secao === 'open-finance' && acessoAoBanco && (
                <section className="mon-secao">
                  <h3>Acesso ao Open Finance</h3>
                  {acessoAoBanco.liberadas.length > 0 && (
                    <>
                      <p className="mon-suporte-meta">Com Open Finance</p>
                      <ul className="mon-suporte">
                        {acessoAoBanco.liberadas.map(p => (
                          <li key={p.userId}>
                            <div className="mon-suporte-topo">
                              <span className="mon-pedido-quem">
                                <strong>{p.nome || p.email || 'conta sem e-mail'}</strong>
                                {p.nome && p.email && <span>{p.email}</span>}
                              </span>
                              <span className="mon-suporte-quando">
                                {p.liberadoAte ? `até ${p.liberadoAte.slice(8, 10)}/${p.liberadoAte.slice(5, 7)}` : ''}
                              </span>
                            </div>
                            <p className="mon-nota">
                              {(p.diasRestantes ?? 0) < 0
                                ? `Venceu há ${-(p.diasRestantes ?? 0)} ${p.diasRestantes === -1 ? 'dia' : 'dias'}. Sem renovar, o banco é desconectado.`
                                : `${p.diasRestantes} ${p.diasRestantes === 1 ? 'dia restante' : 'dias restantes'}.`}
                              {' '}{p.bancos === 0 ? 'Nenhum banco conectado.' : p.bancos === 1 ? '1 banco conectado.' : `${p.bancos} bancos conectados.`}
                            </p>
                            <p className="mon-nota">
                              Plano de {p.maxBancos === 1 ? '1 banco' : `${p.maxBancos} bancos`}.
                            </p>
                            {p.bancos > 0 && (
                              <div className="mon-suporte-acoes">
                                <button onClick={() => desconectarBancos(p)} disabled={agindoNoAcesso !== null} aria-busy={agindoNoAcesso === p.userId}>
                                  Desconectar bancos
                                </button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {acessoAoBanco.orfas === null && (
                    <p className="mon-nota">Não consegui conferir no provedor se há conexões órfãs agora.</p>
                  )}
                  {!!acessoAoBanco.orfas?.length && (
                    <>
                      <p className="mon-suporte-meta">Órfãs no provedor</p>
                      <p className="mon-nota">
                        Vivas no provedor, de uma pessoa, e fora de qualquer conta do app: custam e ninguém vê.
                        Quem está conectando agora aparece aqui por alguns minutos.
                      </p>
                      <ul className="mon-suporte">
                        {acessoAoBanco.orfas.map(o => (
                          <li key={o.conexaoId}>
                            <div className="mon-suporte-topo">
                              <span className="mon-pedido-quem">
                                <strong>{o.nome || o.email || 'conta sem e-mail'}</strong>
                                {o.nome && o.email && <span>{o.email}</span>}
                              </span>
                              <span className="mon-suporte-quando">{o.banco}</span>
                            </div>
                            <div className="mon-suporte-acoes">
                              <button onClick={() => desligarOrfa(o)} disabled={agindoNoAcesso !== null} aria-busy={agindoNoAcesso === o.conexaoId}>
                                Desligar no provedor
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {acessoAoBanco.liberadas.length === 0 && (
                    <p className="mon-nota">Ninguém com Open Finance ainda.</p>
                  )}
                  {recadoDoAcesso && <p className="mon-nota" role="status">{recadoDoAcesso}</p>}
                </section>
              )}

              {/*
                Suporte do Open Finance: o que a pessoa escreveu e a foto do
                diagnóstico. É AQUI que o conteúdo é lido — o e-mail de aviso
                não traz nenhum dos dois.
              */}
              {secao === 'suporte' && (
                <section className="mon-secao">
                  <h3>Suporte do Open Finance</h3>
                  {suporte === null && <p className="mon-nota">Não consegui ler a fila de suporte.</p>}
                  {suporte?.length === 0 && <p className="mon-nota">Nenhum pedido na fila.</p>}
                  <ul className="mon-suporte">
                    {(suporte ?? []).map(p => (
                      <li key={p.id}>
                        <div className="mon-suporte-topo">
                          <span className="mon-pedido-quem">
                            <strong>{p.nome || p.email || 'conta sem e-mail'}</strong>
                            {p.nome && p.email && <span>{p.email}</span>}
                          </span>
                          <span className="mon-suporte-quando">{new Date(p.criadoEm).toLocaleDateString('pt-BR')}</span>
                        </div>
                        <p className="mon-suporte-meta">
                          {NOME_DO_TIPO[p.tipo] ?? p.tipo} · {p.banco}
                          {p.valorCentavos ? ` · R$ ${(p.valorCentavos / 100).toFixed(2).replace('.', ',')}` : ''}
                        </p>
                        <p className="mon-suporte-texto">{p.mensagem}</p>
                        <p className="mon-nota">
                          {p.diagnostico
                            ? (typeof p.diagnostico === 'object' && p.diagnostico !== null && 'erro' in (p.diagnostico as object)
                              ? 'O diagnóstico falhou. Gere de novo.'
                              : 'Diagnóstico pronto.')
                            : 'Diagnóstico ainda não gerado.'}
                        </p>
                        <div className="mon-suporte-acoes">
                          <button onClick={() => copiarPedido(p)} disabled={agindoNoSuporte !== null}>
                            Copiar para investigar
                          </button>
                          {p.email && (
                            <a href={`mailto:${p.email}?subject=${encodeURIComponent('Sobre o seu pedido no FinTracking')}`}>
                              Responder por e-mail
                            </a>
                          )}
                          <button
                            onClick={() => agirNoSuporte(p, () => regerarDiagnosticoDeSuporte(p.id), 'Diagnóstico gerado de novo.')}
                            disabled={agindoNoSuporte !== null}
                            aria-busy={agindoNoSuporte === p.id}
                          >
                            Gerar de novo
                          </button>
                          <button
                            onClick={() => agirNoSuporte(p, () => resolverPedidoDeSuporte(p.id), 'Pedido resolvido. O diagnóstico foi apagado.')}
                            disabled={agindoNoSuporte !== null}
                          >
                            Marcar resolvido
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {recadoDoSuporte && <p className="mon-nota" role="status">{recadoDoSuporte}</p>}
                  <p className="mon-nota">
                    Resolver apaga o diagnóstico. Sem resolver, ele some sozinho em 30 dias.
                  </p>
                </section>
              )}

              {/*
                Cada linha é uma conta a mais falando com o mesmo número do
                bot: liberar é uma decisão por pessoa, por isso um botão por
                linha e nenhum "liberar todos".
              */}
              {secao === 'whatsapp' && (
                <section className="mon-secao">
                  <h3>Pedidos de WhatsApp</h3>
                  {pedidos === null && <p className="mon-nota">Não consegui ler os pedidos.</p>}
                  {pedidos?.length === 0 && <p className="mon-nota">Ninguém esperando liberação.</p>}
                  <ul className="mon-pedidos">
                    {(pedidos ?? []).map(p => (
                      <li key={p.userId}>
                        <span className="mon-pedido-quem">
                          <strong>{p.nome || p.email}</strong>
                          {p.nome && <span>{p.email}</span>}
                        </span>
                        <button
                          className="mon-pedido-liberar"
                          onClick={() => liberar(p)}
                          disabled={liberando !== null}
                          aria-busy={liberando === p.userId}
                        >
                          Liberar
                        </button>
                      </li>
                    ))}
                  </ul>
                  {erroDoPedido && <p className="mon-erro">{erroDoPedido}</p>}
                  {semAviso && (
                    <p className="mon-erro" role="status">
                      {semAviso} foi liberada, mas o email de aviso não saiu.
                      Vale avisar por outro caminho.
                    </p>
                  )}
                  <p className="mon-nota">
                    Quem é liberado passa a poder parear o próprio número, e
                    recebe um email avisando, com o passo a passo.
                  </p>
                </section>
              )}

              {secao === 'sugestoes' && !m.sugestoes && <p className="mon-nota">Sem dados de sugestões.</p>}
              {secao === 'sugestoes' && m.sugestoes && (
                <section className="mon-secao">
                  <h3>Sugestões</h3>
                  {m.sugestoes.total === 0 ? (
                    <p className="mon-nota">
                      Nenhuma nesta semana. Elas chegam pelo WhatsApp, quando
                      alguém pede algo que o app não faz, e viram um resumo por
                      email toda segunda.
                    </p>
                  ) : (
                    <>
                      <div className="mon-numeros">
                        <div><strong>{m.sugestoes.total}</strong><span>nesta semana</span></div>
                        <div><strong>{m.sugestoes.naFila}</strong><span>no próximo resumo</span></div>
                        {/* Só aparece quando existe. Um zero permanente ao lado
                            dos outros números vira parte do cenário, e aí o dia
                            em que ele virar 3 passa despercebido. */}
                        {m.sugestoes.atrasadas > 0 && (
                          <div><strong>{m.sugestoes.atrasadas}</strong><span>ficaram fora</span></div>
                        )}
                      </div>
                      {/*
                        * A lista fica RECOLHIDA (16/09, pedido dela: "deixe as
                        * sugestões num link separado pra não tomar espaço no
                        * painel").
                        *
                        * O que continua à vista são os números e o alarme: uma
                        * sugestão que ficou fora do resumo é a única coisa aqui
                        * que pede ação, e ela não pode depender de um clique.
                        * `details` porque o navegador já faz isto com teclado e
                        * leitor de tela.
                        */}
                      <details className="mon-sugs-det">
                        <summary>
                          Ver {m.sugestoes.mostrando === 1 ? 'a sugestão' : `as ${m.sugestoes.mostrando} sugestões`}
                          {m.sugestoes.mostrando < m.sugestoes.total && ` mais recentes de ${m.sugestoes.total}`}
                        </summary>
                        <div className="mon-sugs">
                          {m.sugestoes.itens.map((s, i) => <LinhaSugestao key={i} s={s} />)}
                        </div>
                      </details>
                      <p className="mon-nota">
                        O resumo sai <strong>segunda de manhã</strong>, agrupando
                        pedidos parecidos, para o primeiro endereço de
                        ADMIN_EMAILS. A lista recomeça toda segunda: o que já foi
                        num resumo de outra semana sai daqui, o que ainda não foi
                        fica até ir. Se algo aparecer como{' '}
                        <strong>ficou fora do resumo</strong>, o envio falhou.
                      </p>
                    </>
                  )}
                </section>
              )}

              {secao === 'infra' && <section className="mon-secao">
                <h3>Infraestrutura</h3>
                <div className={`mon-linha ${m.banco.ok ? 'mon-v-ok' : 'mon-v-falhando'}`}>
                  <span>Banco</span>
                  <span>{m.banco.ok ? `responde em ${m.banco.latenciaMs}ms` : (m.banco.erro ?? 'fora')}</span>
                </div>
                {m.canal && (
                  <div className={`mon-linha ${m.canal.estado === 'no-ar' ? 'mon-v-ok'
                    : m.canal.estado === 'fora-do-ar' ? 'mon-v-atrasado' : ''}`}>
                    <span>WhatsApp</span>
                    <span>
                      {m.canal.estado === null ? 'ainda não medido'
                        : m.canal.estado === 'no-ar' ? 'no ar' : 'fora do ar'}
                      {m.canal.diasForaDoAr > 0 && ` · ${m.canal.diasForaDoAr}d fora em 14`}
                    </span>
                  </div>
                )}
                {m.audio && (
                  <div className={`mon-linha ${m.audio.ouve ? 'mon-v-ok' : 'mon-v-atrasado'}`}>
                    <span>Áudio no WhatsApp</span>
                    <span>{m.audio.ouve ? 'ouve' : 'sem chave da OpenAI, responde “manda escrito”'}</span>
                  </div>
                )}
                {/*
                  * A proteção do banco, antes do volume: é a linha que decide
                  * se qualquer outro número desta tela é confiável. Um app que
                  * vaza dado entre contas não tem "saúde" a reportar.
                  */}
                {m.protecao && (
                  <div className={`mon-linha ${m.protecao.protegido ? 'mon-v-ok' : 'mon-v-falhando'}`}>
                    <span>Isolamento entre contas</span>
                    <span>
                      {/* 16/09: era "faltam políticas em {esperado − comPolitica}",
                          com o esperado escrito à mão. Agora os dois buracos vêm
                          do catálogo do banco e cada um diz o que significa. */}
                      {m.protecao.protegido
                        ? `protegido pelo banco · ${m.protecao.comPolitica} tabelas`
                        : m.protecao.conexaoIgnoraRls
                          ? 'só o código protege, a conexão ignora as políticas'
                          : m.protecao.semRls > 0
                            ? `${m.protecao.semRls} tabela(s) sem RLS`
                            : `${m.protecao.semPolitica} tabela(s) com RLS e sem política`}
                    </span>
                  </div>
                )}
                {m.protecao && !m.protecao.protegido && (
                  <p className="mon-nota">{m.protecao.motivo}</p>
                )}
                {m.volume7d && (
                  <div className="mon-linha">
                    <span>Lançamentos (7d)</span>
                    <span>
                      {m.volume7d.total}
                      {Object.entries(m.volume7d.porOrigem).length > 0 &&
                        ` · ${Object.entries(m.volume7d.porOrigem).map(([k, v]) => `${v} ${k}`).join(', ')}`}
                    </span>
                  </div>
                )}
              </section>}
            </>
          )}

          <p className="mon-nota mon-rodape">
            Erros de aplicação ficam no Sentry. Este painel cobre o que o
            Sentry não vê: rotina que deveria ter rodado e não rodou.
          </p>
        </main>
      </div>
    </div>
  );
};

export default PaginaDeSaude;
